
import { useRef } from 'react';
import { toast } from '@/components/ui/use-toast';
import { ChatInput } from './chat/ChatInput';
import { Header } from './chat/Header';
import { LoadingAnimation } from './chat/LoadingAnimation';
import { ChatContainer } from './chat/ChatContainer';
import { useSession } from '@/hooks/useSession';
import { handleTestCommand } from '@/utils/chatTestCommands';

export const Chat = () => {
  const { 
    messages, 
    isLoading, 
    isInitializing, 
    isNewConversation, 
    error, 
    isSharedSession, 
    sendMessage, 
    clearChat, 
    shareSession 
  } = useSession();

  const handleShare = () => {
    const shareUrl = shareSession();
    if (!shareUrl) return;
    
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        toast({
          title: "Link copied!",
          description: "Share this link to continue the conversation in another browser.",
        });
      })
      .catch(() => {
        toast({
          title: "Failed to copy link",
          description: "Please try again",
          variant: "destructive",
        });
      });
  };

  const handleSubmit = async (userMessage: string) => {
    if (userMessage.startsWith('/test')) {
      const command = userMessage.split(' ')[1];
      const testMessage = handleTestCommand(command);
      if (testMessage) {
        sendMessage(userMessage);
        return;
      }
    }

    sendMessage(userMessage);
  };

  if (isInitializing || isNewConversation) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-blue-50/30">
        <div className="flex-1 p-4">
          <LoadingAnimation 
            onComplete={() => {
              if (isInitializing) {
                // This is now handled by the hook
              }
            }} 
            isNewConversation={isNewConversation}
          />
          {error && (
            <div className="text-center text-destructive mt-4">
              {error}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 px-4 py-3 bg-background/90 backdrop-blur-lg border-t border-border/40">
          <div className="max-w-3xl mx-auto flex gap-3">
            <div className="flex-1">
              <div className="h-10 w-full bg-muted/50 animate-pulse rounded-lg" />
            </div>
            <div className="h-10 w-20 bg-muted/50 animate-pulse rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-blue-50/30">
      <Header onClearChat={clearChat} onShare={handleShare} />
      <ChatContainer 
        messages={messages}
        isLoading={isLoading}
        isSharedSession={isSharedSession}
      />
      <ChatInput onSubmit={handleSubmit} isLoading={isLoading || isInitializing} />
    </div>
  );
};
